import { embedOverlayMetadata, stripOverlayMetadata } from './overlayMetadata';
import { writeStandardAnnotations } from './pdfAnnotations';

/**
 * Unified persistence tail for every save path that produces editable overlays.
 *
 * Every save-producing feature (Viewer save, Sign apply, any future tool) must
 * route through this helper instead of calling embed/write directly. Two
 * artifacts are always emitted together when preserveOverlays is true:
 *
 *   1. ScrivarOverlays sidecar in the Info dict — authoritative for re-editing
 *      when reopened in Scrivar.
 *   2. Standard PDF /Annot dictionaries (with /AP appearance streams) for
 *      EVERY overlay type — text edits included — so Acrobat / Preview / other
 *      viewers render the overlays as real annotations. On a non-flatten save,
 *      text edits are NOT baked into the content stream; the /FreeText /AP
 *      annot is the only artifact for them.
 *
 * When preserveOverlays is false (flatten path), the sidecar is stripped and
 * no /Annot writes happen — the baked pixels are the only artifact.
 *
 * @param {import('pdf-lib').PDFDocument} doc
 * @param {Array} edits
 * @param {Array} annotations
 * @param {{ preserveOverlays?: boolean }} options
 */
export async function persistOverlaysToDoc(doc, edits, annotations, { preserveOverlays = true } = {}) {
  if (!preserveOverlays) {
    try { await stripOverlayMetadata(doc); } catch (_) {}
    // Deliberately skip writeStandardAnnotations — flatten means "bake pixels,
    // leave no editable handles behind".
    return;
  }

  const editCount = edits?.length || 0;
  const annotCount = annotations?.length || 0;
  if (editCount + annotCount === 0) return;

  await embedOverlayMetadata(doc, edits || [], annotations || [], doc.getPageCount());
  // Appearance and editable metadata are one transaction. Missing appearances
  // are a failed save, even if Scrivar's sidecar could still display the text.
  await writeStandardAnnotations(doc, edits || [], annotations || []);
}
